/**
 * Generate a URL-friendly slug from a string
 * @param {string} text - The text to convert to a slug
 * @returns {string} - URL-friendly slug
 */
export function generateSlug(text) {
  if (!text) return '';

  return (
    text
      .toString()
      .toLowerCase()
      .trim()
      // Replace spaces with hyphens
      .replace(/\s+/g, '-')
      // Remove special characters except hyphens
      .replace(/[^\w\-]+/g, '')
      // Replace multiple hyphens with single hyphen
      .replace(/\-\-+/g, '-')
      // Remove leading/trailing hyphens
      .replace(/^-+/, '')
      .replace(/-+$/, '')
      // Limit length for URL friendliness
      .substring(0, 100)
  );
}

/**
 * Generate a full SEO-friendly URL path for a discussion
 * Format: /forum/community-slug/discussion/slug-title-oid (with community)
 * Format: /forum/discussion/slug-title-oid (legacy, no community)
 * @param {object} discussion - Discussion object with title, oid, and optional communitySlug
 * @param {string} communitySlug - Optional community slug override
 * @returns {string} - Full path for the discussion
 */
export function getDiscussionPath(discussion, communitySlug = null) {
  if (!discussion?.oid) return '/forum';

  const slug = generateSlug(discussion.title || 'discussion');
  const community = communitySlug || discussion.communitySlug;

  if (community) {
    return `/forum/${community}/discussion/${slug}-${discussion.oid}`;
  }

  return `/forum/discussion/${slug}-${discussion.oid}`;
}

/**
 * Generate a full SEO-friendly URL path for a blog article
 * Format: /blog/post/slug-title-oid
 * @param {object} article - Article object with title and oid
 * @returns {string} - Full path for the article
 */
export function getArticlePath(article) {
  if (!article?.oid) return '/blog';

  const slug = generateSlug(article.title || 'article');
  return `/blog/post/${slug}-${article.oid}`;
}

/**
 * Extract the OID from a slug-based URL parameter
 * The OID is always the last part after the final hyphen
 * @param {string} param - The URL parameter (e.g., "my-discussion-title-abc123")
 * @returns {string} - The extracted OID
 */
export function extractOidFromSlug(param) {
  if (!param) return '';

  // Check if it's just an OID (no hyphens or very short)
  if (!param.includes('-') || param.length < 10) {
    return param;
  }

  // The OID is the last part after the final hyphen
  const lastHyphenIndex = param.lastIndexOf('-');
  if (lastHyphenIndex === -1) return param;

  // Get potential OID
  const potentialOid = param.substring(lastHyphenIndex + 1);

  // If the potential OID looks like a valid OID (alphanumeric, reasonable length)
  // return it, otherwise return the full param (it might just be the OID)
  if (potentialOid && potentialOid.length >= 5) {
    return potentialOid;
  }

  return param;
}
